import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { blogAPI } from '../../services/api';
import { getStorageUrl } from '../../config/api';
import Header from '../Header';
import BottomNavigationBar from '../BottomNavigationBar';
import Footer from '../Footer';
import './BlogPage.css';

const BlogPage = () => {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);

  useEffect(() => {
    fetchBlogs();
  }, [currentPage, searchTerm]);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {
        page: currentPage,
        per_page: 12,
        ...(searchTerm && { search: searchTerm })
      };
      const response = await blogAPI.getBlogs(params);
      if (response.success) {
        // Handle both old and new response formats
        const blogsData = response.data || response.data?.data || [];
        setBlogs(Array.isArray(blogsData) ? blogsData : []);
        
        // Handle pagination from meta or direct response
        if (response.meta) {
          setPagination({
            current_page: response.meta.current_page,
            last_page: response.meta.last_page,
            per_page: response.meta.per_page,
            total: response.meta.total,
            from: response.meta.from,
            to: response.meta.to,
            links: response.links
          });
        } else if (response.data && response.data.current_page) {
          setPagination(response.data);
        }
      } else {
        setError(response.message || 'Failed to load blogs');
      }
    } catch (err) {
      const errorMessage = err.message || err.response?.data?.message || 'Failed to load blogs. Please try again later.';
      setError(errorMessage);
      console.error('Error fetching blogs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchBlogs();
  };

  const handleHomeClick = () => {
    navigate('/');
  };

  const handleCategoriesClick = () => {
    navigate('/categories');
  };

  const handleHowItWorksClick = () => {
    navigate('/#how-it-works');
  };

  const handleProfileClick = () => {
    navigate('/profile');
  };

  const handleAccountSettingsClick = () => {
    navigate('/settings');
  };

  const handleForumClick = () => {
    navigate('/forum');
  };

  const handleBlogClick = () => {
    navigate('/blog');
  };

  const handleLoginClick = () => {
    navigate('/login');
  };

  const handleSignupClick = () => {
    navigate('/signup');
  };

  const handleJobsClick = () => {
    navigate('/vacancies');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const truncateText = (text, maxLength = 150) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  if (authLoading) {
    return (
      <div className="App">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          fontSize: '18px',
          color: '#666'
        }}>
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div className="App blog-page-wrapper">
      <div className="blog-header">
        <Header 
          onLoginClick={handleLoginClick}
          onSignupClick={handleSignupClick}
          isLoggedIn={isAuthenticated}
          user={user}
          onHomeClick={handleHomeClick}
          onCategoriesClick={handleCategoriesClick}
          onHowItWorksClick={handleHowItWorksClick}
          onProfileClick={handleProfileClick}
          onAccountSettingsClick={handleAccountSettingsClick}
          onForumClick={handleForumClick}
          currentPage="blog"
        />
      </div>

      <div className="blog-content">
        <div className="blog-container">
          <div className="blog-page-header">
            <h1>Blog</h1>
            <p className="blog-subtitle">Stay updated with our latest articles and insights</p>
          </div>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="blog-search-form">
            <input
              type="text"
              placeholder="Search blogs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="blog-search-input"
            />
            <button type="submit" className="blog-search-button">
              Search
            </button>
          </form>

          {/* Loading State */}
          {loading && (
            <div className="blog-loading">
              <p>Loading blogs...</p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="blog-error">
              <p>{error}</p>
              <button onClick={fetchBlogs}>Try Again</button>
            </div>
          )}

          {/* Blog List */}
          {!loading && !error && (
            <>
              {blogs.length === 0 ? (
                <div className="blog-empty">
                  <p>No blog posts found.</p>
                  {searchTerm && (
                    <button onClick={() => { setSearchTerm(''); setCurrentPage(1); }} className="blog-clear-search">
                      Clear search
                    </button>
                  )}
                </div>
              ) : (
                <div className="blog-grid">
                  {blogs.map((blog) => (
                    <article key={blog.id} className="blog-card">
                      {blog.featured_image && (
                        <div className="blog-card-image">
                          <img 
                            src={getStorageUrl(blog.featured_image)} 
                            alt={blog.title}
                            onError={(e) => {
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      )}
                      <div className="blog-card-content">
                        <h2 className="blog-card-title">
                          <Link to={`/blog/${blog.slug}`}>{blog.title}</Link>
                        </h2>
                        {blog.excerpt && (
                          <p className="blog-card-excerpt">{truncateText(blog.excerpt)}</p>
                        )}
                        <div className="blog-card-meta">
                          <span className="blog-card-date">{formatDate(blog.created_at)}</span>
                          <div className="blog-card-stats">
                            <span className="blog-stat">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                <circle cx="12" cy="12" r="3"></circle>
                              </svg>
                              {blog.views_count || 0}
                            </span>
                            <span className="blog-stat">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                              </svg>
                              {blog.comments_count || 0}
                            </span>
                            <span className="blog-stat">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M14 9V5a3 3 0 0 0-6 0v4"></path>
                                <rect x="2" y="9" width="20" height="12" rx="2"></rect>
                                <circle cx="12" cy="15" r="1"></circle>
                              </svg>
                              {(blog.likes_count || 0) - (blog.dislikes_count || 0)}
                            </span>
                          </div>
                        </div>
                        <Link to={`/blog/${blog.slug}`} className="blog-card-read-more">
                          Read More →
                        </Link>
                      </div>
                    </article>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {pagination && pagination.last_page > 1 && (
                <div className="blog-pagination">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="blog-pagination-btn"
                  >
                    Previous
                  </button>
                  <span className="blog-pagination-info">
                    Page {currentPage} of {pagination.last_page}
                  </span>
                  <button
                    onClick={() => setCurrentPage(prev => Math.min(pagination.last_page, prev + 1))}
                    disabled={currentPage === pagination.last_page}
                    className="blog-pagination-btn"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <Footer />
      
      <BottomNavigationBar
        onHomeClick={handleHomeClick}
        onLoginClick={handleLoginClick}
        onSignupClick={handleSignupClick}
        onProfileClick={handleProfileClick}
        onAccountSettingsClick={handleAccountSettingsClick}
        onJobsClick={handleJobsClick}
        onForumClick={handleForumClick}
      />
    </div>
  );
};

export default BlogPage;
